import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { Box, IconButton, InputAdornment, Typography } from '@mui/material'
import React, { useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { countStringBytes, doesStringContainEmojis } from '../../../utils'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { TextField } from 'src/Libs/inputs/react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useDialogActions } from '../../../components/Dialogs/hooks'

export interface EnterOcppPasswordDialogProps {
  setPassword: (password: string) => void
  onClose?: () => void
}

/** Dialog for entering OCPP password on charging stations */
export const EnterOcppPasswordDialog: React.FC<EnterOcppPasswordDialogProps> = ({ setPassword, onClose }) => {
  const { t } = useCustomTranslation()

  const validationSchema = useValidationSchema()
  const methods = useForm({
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
    reValidateMode: 'onChange',
    defaultValues: { password: '' },
  })
  const { handleSubmit, formState } = methods

  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const toggleVisible = (): void => setIsPasswordVisible(visible => !visible)

  const onSubmit: SubmitHandler<{ password: string }> = data => {
    setPassword(data.password)
    onClose?.()
  }

  const handleOnClose = (): void => {
    onClose?.()
  }

  const dialogActions = useDialogActions({
    primary: {
      name: t('common.save'),
      onClick: handleSubmit(onSubmit),
      disabled: !formState.isValid,
    },
    secondary: { onClick: handleOnClose },
  })

  return (
    <FormProvider {...methods}>
      <Dialog
        maxWidth={'md'}
        onClose={handleOnClose}
        title={t('installation.evseEditor.carAtHome.passwordConfig.popupTitle')}
        actions={dialogActions}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', py: 2 }} component={'form'} onSubmit={e => handleSubmit(onSubmit)(e)}>
          <TextField
            maxLength={63}
            name="password"
            label={t('common.password')}
            type={isPasswordVisible ? undefined : 'password'}
            fullWidth
            endAdornment={
              <InputAdornment position="end">
                <IconButton aria-label={isPasswordVisible ? 'hide the password' : 'display the password'} onClick={toggleVisible} color="inherit">
                  {isPasswordVisible ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
          <Typography variant="caption" sx={{ pt: 1.5, display: 'inline-block' }}>
            {t('installation.evseEditor.carAtHome.passwordConfig.info')}
          </Typography>
        </Box>
      </Dialog>
    </FormProvider>
  )
}

//#region ValidationSchema
const useValidationSchema = (): z.Schema<{ password: string }> => {
  const { t } = useCustomTranslation()

  const validationSchema = z.object({
    password: z
      .string()
      .min(1)
      .max(63)
      .refine(password => {
        if (doesStringContainEmojis(password) || countStringBytes(password) >= 64) {
          return t('installation.evseEditor.carAtHome.PasswordError')
        }
        return true
      }),
  })

  return validationSchema
}
//#endregion
